import { SearchResult } from "#next/utils/interfaces";
import { styled } from "@mui/material/styles";
import {
  Rating,
  Box,
  Popover,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import React, { useContext } from "react";
import { PostContext } from "#next/components/post/contexts";
import InfoIcon from "@mui/icons-material/Info";
import CircleIcon from "@mui/icons-material/Circle";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";

const mapLanguage = (score: number) => {
  return Math.min(Math.round(score * 5), 5);
};

// Average "negative" sentiment score on the platform is ~0.67. We end up
// showing too many posts as negative, so we boost the thresholds.
const mapNegativeLangauge = (score: number) => {
  if (score >= 0.9) return 5;
  if (score < 0.9 && score >= 0.8) return 4;
  if (score < 0.8 && score >= 0.7) return 3;
  if (score < 0.7 && score >= 0.5) return 2;
  else return 1;
};

const mapRelevance = (score: number) => {
  if (score >= 0.8) return 5;
  if (score < 0.8 && score >= 0.7) return 4;
  if (score < 0.7 && score >= 0.6) return 3;
  if (score < 0.6 && score >= 0.3) return 2;
  else return 1;
};

const routeMap = (score: number, type: string) => {
  if (type === "Relevance") return mapRelevance(score);
  if (type === "Negativity") return mapNegativeLangauge(score);
  else return mapLanguage(score);
};

const mapColors = (rating: number) => {
  if (rating === 5) return "#ff6d75";
  if (rating < 3) return "#fde09d";
  else return "#faaf00";
};

const mapLabels = (rating: number) => {
  if (rating === 5) return "high";
  if (rating < 3) return "low";
  else return "med";
};

function mapScore(
  score: number | null,
  type: string,
): {
  score: number;
  rating: number;
  color: string;
  label: string;
} {
  const roundedScore = score
    ? Math.round((score + Number.EPSILON) * 100) / 100
    : 0;
  const rating = routeMap(roundedScore, type);
  return {
    score: roundedScore,
    rating: rating,
    color: mapColors(rating),
    label: mapLabels(rating),
  };
}

export const Stats = ({ post }: { post: SearchResult }) => {
  const { displayRelevance } = useContext(PostContext);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      justifyContent="flex-end"
      alignItems="center"
      sx={{ display: "flex", wrap: "nowrap", flexGrow: 1 }}
      // Otherwise clicks get handled by the parent card as well, which opens the SinglePost modal
      onClick={(e) => e.stopPropagation()}
    >
      <IconButton onClick={handleClick}>
        <InfoIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 1,
            },
          },
        }}
      >
        {displayRelevance && (
          <ScoreRating title="Relevance" score={post.normalized_relevance} />
        )}
        {!!post.engagement_rate && (
          <ScoreRating title="Engagement" score={post.engagement_rate} />
        )}
        <ScoreRating title="Negativity" score={post.negative} />
        <ScoreRating title="Offensiveness" score={post.offensive} />
        <ScoreRating title="Hate" score={post.hateful} />
        <ScoreRating title="Violence" score={post.violent} />
      </Popover>
    </Box>
  );
};

function ScoreRating({ title, score }: { title: string; score: number }) {
  const stats = mapScore(score, title);

  const StyledRating = styled(Rating)({
    "& .MuiRating-iconFilled": {
      color: stats.color,
    },
  });

  return (
    <>
      <Typography variant="caption" component="legend">
        {title}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <StyledRating
          size="small"
          icon={<CircleIcon fontSize="inherit" />}
          emptyIcon={<PanoramaFishEyeIcon fontSize="inherit" />}
          name={`${title.toLowerCase()}-rating`}
          value={stats.rating}
          max={5}
          readOnly
        />
        <Tooltip title={stats.score} placement="top" arrow>
          <Typography
            variant="overline"
            sx={{ marginLeft: 2, cursor: "pointer" }}
          >
            {stats.label}
          </Typography>
        </Tooltip>
      </Box>
    </>
  );
}
